import { format, getYear, isValid, parseISO, subYears } from 'date-fns';
import isEqual from 'lodash.isequal';
import React, { useCallback, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { QUERY_PARAM_VALUE } from '_constants/queryParams';
import { STATEMENT_STATUSES } from '_constants/statements';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { usePrevious, useUploadDocumentsQueryParam } from '_hooks';
import {
  selectDataAsMap as selectFileTypeMap,
  selectData as selectFileTypes,
  selectLoading as selectFileTypesLoading,
} from '_store/fileTypes/selector';
import { getFileTypes } from '_store/fileTypes/slice';
import { showModal } from '_store/modals/slice';
import { selectActiveBusinessTaxFilingProductId, selectActivePersonalTaxFilingProductId } from '_store/order/selector';
import {
  selectData,
  selectFetchCounter,
  selectLoading,
  selectParams,
  selectUploading,
} from '_store/statements/selector';
import {
  downloadStatement,
  getStatements,
  refreshForm,
  refreshParams,
  resetFetchCounter,
  uploadStatement,
} from '_store/statements/slice';
import { selectCustomerId } from '_store/user/selector';

import Icon from 'components/atomic/Icon';
import Paginate from 'components/atomic/Paginate';
import Table from 'components/atomic/Table';
import Tabs from 'components/atomic/Tabs';
import UploadButton from 'components/atomic/UploadButton';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import DocList from './DocList';
import DocUploadCards from './DocUploadCards';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledNavContainer = styled.div`
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledEmptyDataContainer = styled.div`
  padding-top: ${theme.spaces.medium}px;
`;

const StyledTableContainer = styled.div`
  padding-top: ${theme.spaces.smallX}px;
`;

const StyledPaginateContainer = styled.div`
  padding-top: ${theme.spaces.large}px;
`;

const StyledStatusContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledStatusIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  width: 100%;
`;

const StyledActionButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 26px;
  padding-top: ${theme.spaces.small2X - 2}px;
  padding-bottom: ${theme.spaces.small2X - 2}px;
  padding-right: ${theme.spaces.small2X}px;
  padding-left: ${theme.spaces.small2X}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  cursor: pointer;

  &:hover {
    border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.16)};
    background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};

    * {
      color: ${theme.colors.indigo.primary};
    }

    svg {
      path {
        stroke: ${theme.colors.indigo.primary};
      }
    }
  }
`;

const StyledFileNameContainer = styled.div`
  width: 100%;

  > * {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const getColumns = ({ fileTypeMap = {}, handleDownload, handleUpload, handleDelete }) => {
  return [
    {
      dataIndex: 'year',
      title: 'Tax Year',
      width: '10%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {row?.year}
        </Text>
      ),
    },
    {
      dataIndex: 'type',
      title: 'Form Type',
      width: '18%',
      render: (row) => (
        <Text size={theme.fontSizes.small} weight={400}>
          {fileTypeMap[row?.type] || row?.type?.replace(/([A-Z])/g, ' $1').trim()}
        </Text>
      ),
    },
    {
      dataIndex: 'download',
      title: 'File Name',
      width: '20%',
      render: (row) => {
        if (row?.status === STATEMENT_STATUSES.requested) {
          return (
            <Text size={theme.fontSizes.small} weight={400}>
              N/A
            </Text>
          );
        }

        return (
          <StyledFileNameContainer onClick={handleDownload(row?.id, row?.fileName)}>
            <Text
              size={theme.fontSizes.small}
              weight={400}
              color={
                row?.status === STATEMENT_STATUSES.requested ? theme.colors.gray.secondary : theme.colors.gray.primary
              }
              hoverColor={theme.colors.indigo.primary}
            >
              {row?.fileName}
            </Text>
          </StyledFileNameContainer>
        );
      },
    },
    {
      dataIndex: 'updated',
      title: 'Updated',
      width: '14%',
      render: (row) => {
        const parsedDate = parseISO(row?.updated);
        return (
          <Text size={theme.fontSizes.small} weight={400}>
            {isValid(parsedDate) ? format(parsedDate, 'MM/dd/yyyy') : row?.startDate}
          </Text>
        );
      },
    },
    {
      dataIndex: 'status',
      title: 'Status',
      width: '18%',
      render: (row) => {
        let displayedStatus = 'Not Uploaded';

        if (row?.status === STATEMENT_STATUSES.underReview) {
          displayedStatus = 'Uploaded';
        }

        if (row?.status === STATEMENT_STATUSES.confirmed) {
          displayedStatus = 'Reviewed';
        }

        return (
          <StyledStatusContainer>
            <StyledStatusIconContainer>
              <Icon
                alias={
                  row?.status !== STATEMENT_STATUSES.requested ? ICON_ALIASES.tickCircleFilled : ICON_ALIASES.slash
                }
                color={
                  row?.status !== STATEMENT_STATUSES.requested
                    ? theme.colors.indigo.primary
                    : theme.colors.gray.secondary
                }
              />
            </StyledStatusIconContainer>
            <Text
              size={theme.fontSizes.small}
              weight={400}
              color={
                row?.status !== STATEMENT_STATUSES.requested ? theme.colors.indigo.primary : theme.colors.gray.secondary
              }
            >
              {displayedStatus}
            </Text>
          </StyledStatusContainer>
        );
      },
    },
    {
      dataIndex: 'upload',
      title: null,
      width: '12%',
      render: (row) => (
        <StyledActionsContainer>
          <UploadButton
            renderAs={(props) => (
              <StyledActionButton {...props}>
                <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
                  {row?.status === STATEMENT_STATUSES.requested ? 'Add File' : 'Update File'}
                </Text>
              </StyledActionButton>
            )}
            onChange={handleUpload(row?.id)}
          />
        </StyledActionsContainer>
      ),
    },
    {
      dataIndex: 'delete',
      title: null,
      width: '8%',
      render: (row) => (
        <StyledActionsContainer>
          <StyledActionButton onClick={handleDelete(row?.id)}>
            <Text size={theme.fontSizes.smallX} weight={600} color={theme.colors.gray.secondary}>
              Delete
            </Text>
          </StyledActionButton>
        </StyledActionsContainer>
      ),
    },
  ];
};

const TaxDocumentsView = () => {
  const [uploadDocumentsParamValue, setUploadDocumentsParamValue] = useUploadDocumentsQueryParam();
  const prevUploadDocumentsParamValue = usePrevious(uploadDocumentsParamValue);
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const businessTaxFilingProductId = useSelector(selectActiveBusinessTaxFilingProductId);
  const personalTaxFilingProductId = useSelector(selectActivePersonalTaxFilingProductId);
  const prevBusinessTaxFilingProductId = usePrevious(businessTaxFilingProductId);
  const prevPersonalTaxFilingProductId = usePrevious(personalTaxFilingProductId);
  const params = useSelector(selectParams);
  const prevParams = usePrevious(params);
  const loading = useSelector(selectLoading);
  const uploading = useSelector(selectUploading);
  const data = useSelector(selectData);
  const fetchCounter = useSelector(selectFetchCounter);
  const fileTypes = useSelector(selectFileTypes);
  const fileTypeMap = useSelector(selectFileTypeMap);
  const fileTypesLoading = useSelector(selectFileTypesLoading);

  const productId =
    uploadDocumentsParamValue === QUERY_PARAM_VALUE.business ? businessTaxFilingProductId : personalTaxFilingProductId;

  const prevProductId =
    uploadDocumentsParamValue === QUERY_PARAM_VALUE.business
      ? prevBusinessTaxFilingProductId
      : prevPersonalTaxFilingProductId;

  const handleSelectTab = (tabId) => {
    setUploadDocumentsParamValue(tabId);
  };

  const handlePageChange = (page) => {
    dispatch(refreshParams({ page }));
  };

  const handleDelete = useCallback(
    (statementId) => {
      dispatch(showModal({ key: MODAL_KEYS.statementDelete, data: { productId, statementId } }));
    },
    [productId]
  );

  const handleDownload = useCallback(
    (statementId, fileName) => {
      dispatch(downloadStatement({ customerId, productId, statementId, fileName }));
    },
    [customerId, productId]
  );

  const handleAdd = useCallback(
    (fileTypeCode) => {
      dispatch(refreshForm({ [FIELD_KEYS.fileType]: fileTypeCode }));
      dispatch(showModal({ key: MODAL_KEYS.documentUpload, data: { productId } }));
    },
    [productId]
  );

  const handleUpload = useCallback(
    (statementId, file) => {
      dispatch(uploadStatement({ customerId, productId, statementId, file }));
    },
    [customerId, productId]
  );

  useEffect(() => {
    if (customerId && prevProductId && prevProductId === productId && !isEqual(prevParams, params)) {
      dispatch(getStatements({ customerId, productId }));
    }
  }, [customerId, productId, prevParams, params]);

  useEffect(() => {
    if (customerId && prevProductId && productId && prevProductId !== productId) {
      dispatch(getStatements({ customerId, productId }));
      dispatch(getFileTypes({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId]);

  useEffect(() => {
    if (customerId && productId && !fetchCounter) {
      dispatch(refreshParams({ count: 5, year: subYears(new Date(), 1), type: null }));
      dispatch(getStatements({ customerId, productId }));
      dispatch(getFileTypes({ customerId, productId }));
    }
  }, [customerId, productId, fetchCounter]);

  useEffect(() => {
    if (prevUploadDocumentsParamValue && prevUploadDocumentsParamValue !== uploadDocumentsParamValue) {
      dispatch(resetFetchCounter());
    }
  }, [prevUploadDocumentsParamValue, uploadDocumentsParamValue]);

  return (
    <Card>
      <StyledHeader>
        <Text size={theme.fontSizes.medium} weight={600} bottomSpace={theme.spaces.large}>
          Upload {getYear(subYears(new Date(), 1))} Documents
        </Text>
      </StyledHeader>
      <StyledNavContainer>
        <Tabs
          selected={uploadDocumentsParamValue}
          items={[
            {
              tabId: QUERY_PARAM_VALUE.personal,
              iconAlias: ICON_ALIASES.profileCircle,
              title: 'Personal',
            },
            {
              tabId: QUERY_PARAM_VALUE.business,
              iconAlias: ICON_ALIASES.home,
              title: 'Business',
            },
          ]}
          onSelect={handleSelectTab}
        />
      </StyledNavContainer>
      <DocUploadCards loading={fileTypesLoading} items={fileTypes} onAdd={handleAdd} />
      {!isMobile && (
        <StyledTableContainer>
          <Table
            columns={getColumns({
              fileTypeMap,
              handleDownload: (id, file) => () => handleDownload(id, file),
              handleUpload: (id) => (file) => handleUpload(id, file),
              handleDelete: (id) => () => handleDelete(id),
            })}
            rows={data?.elements}
            loading={loading}
            skeletonRowsNum={data?.maxPerPage}
          />
          {!data?.totalPages && (
            <StyledEmptyDataContainer>
              <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
                No data available
              </Text>
            </StyledEmptyDataContainer>
          )}
          <StyledPaginateContainer>
            <Paginate
              pageRangeDisplayed={3}
              pageCount={data?.totalPages}
              currentPage={data?.page}
              onPageChange={handlePageChange}
            />
          </StyledPaginateContainer>
        </StyledTableContainer>
      )}
      {isMobile && (
        <div>
          <DocList
            fileTypeMap={fileTypeMap}
            loading={loading || uploading}
            items={data.elements}
            onUpload={handleUpload}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
          <StyledPaginateContainer>
            <Paginate
              pageRangeDisplayed={0}
              pageCount={data?.totalPages}
              currentPage={data?.page}
              onPageChange={handlePageChange}
            />
          </StyledPaginateContainer>
        </div>
      )}
    </Card>
  );
};

export default TaxDocumentsView;
